/**
 * Config store — localStorage persistence for portal configs.
 * Used by the /config admin page.
 */

export interface EyePositions {
  left: { x: number; y: number };
  right: { x: number; y: number };
}

export interface PortalConfigData {
  id: string;
  name: string;
  subtitle: string;
  imageSrc: string;
  pinealGland: { x: number; y: number };
  haloTranslate: { x: number; y: number };
  haloScale: number;
  imageScale: number;
  imageOffsetX: number;
  fadeStart: number;
  archPointedness: number;
  archStart: number;
  frameThickness: number;
  eyePositions: EyePositions | null;
  haloConfigKey: string;
  portalPresetKey: string;
}

const STORAGE_KEY = 'eternity-portal-configs';
const AUTH_KEY = 'eternity-config-auth';
const PASSWORD = 'rGsq@290';

/** Shared with publish endpoint — single source of truth for the admin password. */
export const PUBLISH_PASSWORD = PASSWORD;

export const DEFAULT_PORTALS: PortalConfigData[] = [
  {
    id: 'giordanoBruno',
    name: 'Giordano Bruno',
    subtitle: 'The Heretic of Infinity',
    imageSrc: '/images/bruno.png',
    pinealGland: { x: 0.50, y: 0.28 },
    haloTranslate: { x: 0, y: 140 },
    haloScale: 1.1,
    imageScale: 0.85,
    imageOffsetX: 0,
    fadeStart: 0.65,
    archPointedness: 0.22,
    archStart: 0.33,
    frameThickness: 14,
    eyePositions: null,
    haloConfigKey: 'giordanoBruno',
    portalPresetKey: 'giordanoBruno',
  },
  {
    id: 'makarios',
    name: 'Makarios III',
    subtitle: 'Μακάριος Γ΄ — The Blessed One',
    imageSrc: '/images/makarios.png',
    pinealGland: { x: 0.48, y: 0.47 },
    haloTranslate: { x: -5, y: 5 },
    haloScale: 1.1,
    imageScale: 0.7,
    imageOffsetX: 0,
    fadeStart: 0.68,
    archPointedness: 0.22,
    archStart: 0.33,
    frameThickness: 14,
    eyePositions: null,
    haloConfigKey: 'makarios',
    portalPresetKey: 'makarios',
  },
  {
    id: 'tesla',
    name: 'Nikola Tesla',
    subtitle: 'Светлост у Тами — The Light in Darkness',
    imageSrc: '/images/tesla.png',
    pinealGland: { x: 0.50, y: 0.32 },
    haloTranslate: { x: 0, y: 0 },
    haloScale: 1.05,
    imageScale: 0.8,
    imageOffsetX: 0,
    fadeStart: 0.68,
    archPointedness: 0.22,
    archStart: 0.33,
    frameThickness: 14,
    eyePositions: null,
    haloConfigKey: 'tesla',
    portalPresetKey: 'tesla',
  },
  {
    id: 'bruceLee',
    name: 'Bruce Lee',
    subtitle: '李小龍 — The Dragon',
    imageSrc: '/images/brucelee.png',
    pinealGland: { x: 0.50, y: 0.30 },
    haloTranslate: { x: -15, y: 30 },
    haloScale: 0.95,
    imageScale: 0.75,
    imageOffsetX: 0,
    fadeStart: 0.68,
    archPointedness: 0.22,
    archStart: 0.33,
    frameThickness: 14,
    eyePositions: null,
    haloConfigKey: 'bruceLee',
    portalPresetKey: 'bruceLee',
  },
  {
    id: 'fuller',
    name: 'Richard Buckminster Fuller',
    subtitle: 'Architect of Universe',
    imageSrc: '/images/fuller.png',
    pinealGland: { x: 0.50, y: 0.35 },
    haloTranslate: { x: 0, y: 0 },
    haloScale: 1.0,
    imageScale: 0.8,
    imageOffsetX: 0,
    fadeStart: 0.68,
    archPointedness: 0.22,
    archStart: 0.33,
    frameThickness: 14,
    eyePositions: null,
    haloConfigKey: 'fuller',
    portalPresetKey: 'fuller',
  },
];

export function loadConfigs(): PortalConfigData[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return DEFAULT_PORTALS;
}

export function saveConfigs(configs: PortalConfigData[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
}

export function isAuthenticated(): boolean {
  return sessionStorage.getItem(AUTH_KEY) === 'true';
}

export function authenticate(password: string): boolean {
  if (password === PASSWORD) {
    sessionStorage.setItem(AUTH_KEY, 'true');
    return true;
  }
  return false;
}
